import React, { useEffect, useMemo, useState } from "react";
import {
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Box,
} from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import "dayjs/locale/ru";
import { fetchWithToken } from "../../../api/apiutils";

// Период (в месяцах) по названию пакета
const getMonthsByName = (name = "") => {
  const n = name.toLowerCase();
  if (n.includes("полугод")) return 6;
  if (n.includes("годов")) return 12;
  if (n.includes("ежемесяч") || n.includes("месяч")) return 1;
  return null;
};

// Запасной вариант — по количеству пакетов одного типа и их порядку
// 3 пакета: месяц / полгода / год, 2 пакета: месяц / год
const getMonthsByIndex = (index, count) => {
  if (count === 3) return [1, 6, 12][index] ?? 1;
  if (count === 2) return [1, 12][index] ?? 1;
  return 1;
};

const UserSubscription = ({ open, onClose, userName }) => {
  const [packages, setPackages] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [loading, setLoading] = useState(false);
  const [typesLoaded, setTypesLoaded] = useState(false);
  const [startDate, setStartDate] = useState(dayjs());
  const [expiredAt, setExpiredAt] = useState(null);

  const selectedPackage = useMemo(
    () => packages.find((p) => p.id === selectedId) || null,
    [packages, selectedId],
  );

  // При выборе плана даты проставляются автоматически от текущего момента
  const handlePlanChange = (id) => {
    setSelectedId(id);
    const pkg = packages.find((p) => p.id === id);
    const now = dayjs();
    setStartDate(now);
    setExpiredAt(pkg ? now.add(pkg.months, "month") : null);
  };

  // При смене даты начала дата окончания сдвигается на период плана
  const handleStartChange = (value) => {
    setStartDate(value);
    if (value && value.isValid() && selectedPackage) {
      setExpiredAt(value.add(selectedPackage.months, "month"));
    }
  };

  const getTypes = async () => {
    try {
      const res = await fetchWithToken("/api/shop/packages");
      const resJson = await res.json();
      const data = resJson.data || [];

      // группируем по типу, чтобы знать порядок и количество
      const byType = data.reduce((acc, p) => {
        (acc[p.type] = acc[p.type] || []).push(p);
        return acc;
      }, {});

      const prepared = data.map((p) => {
        const group = byType[p.type];
        const months =
          getMonthsByName(p.name) ??
          getMonthsByIndex(group.indexOf(p), group.length);
        return { ...p, months };
      });

      setPackages(prepared);
      if (prepared.length > 0) {
        const now = dayjs();
        setSelectedId(prepared[0].id);
        setStartDate(now);
        setExpiredAt(now.add(prepared[0].months, "month"));
      }
      setTypesLoaded(true);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getTypes();
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!selectedPackage || !startDate || !expiredAt) return;
    setLoading(true);
    try {
      const payload = {
        userName,
        startDate: startDate.toISOString(),
        expiredAt: expiredAt.toISOString(),
        type: selectedPackage.type,
      };
      console.log("SUBMIT:", payload);

      const res = await fetchWithToken("/api/shop/give/subscription", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const resJson = await res.json();
      console.log(resJson);
      onClose();
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Добавить подписку</DialogTitle>

      <DialogContent>
        <Typography gutterBottom>
          Добавляем подписку для пользователя: <b>{userName}</b>
        </Typography>

        <form onSubmit={onSubmit} id="subscription-form">
          <FormControl fullWidth margin="normal">
            <InputLabel>План</InputLabel>
            <Select
              label="План"
              value={typesLoaded ? selectedId : ""}
              onChange={(e) => handlePlanChange(e.target.value)}
              disabled={loading || !typesLoaded}
            >
              {packages.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name} — {p.price?.toLocaleString("ru-RU")} ₸
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ru">
            <DatePicker
              label="Дата начала"
              value={startDate}
              onChange={handleStartChange}
              disabled={loading}
              inputFormat="DD.MM.YYYY"
              format="DD.MM.YYYY"
              slotProps={{ textField: { fullWidth: true, margin: "normal" } }}
              renderInput={(params) => (
                <TextField {...params} fullWidth margin="normal" />
              )}
            />

            <DatePicker
              label="Дата окончания"
              value={expiredAt}
              onChange={(value) => setExpiredAt(value)}
              disabled={loading}
              minDate={startDate || undefined}
              inputFormat="DD.MM.YYYY"
              format="DD.MM.YYYY"
              slotProps={{ textField: { fullWidth: true, margin: "normal" } }}
              renderInput={(params) => (
                <TextField {...params} fullWidth margin="normal" />
              )}
            />
          </LocalizationProvider>
        </form>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={onClose}
          color="primary"
          variant="outlined"
          disabled={loading}
        >
          Закрыть
        </Button>
        <Box sx={{ position: "relative" }}>
          <Button
            type="submit"
            form="subscription-form"
            color="primary"
            variant="contained"
            disabled={loading || !selectedPackage}
          >
            Сохранить
          </Button>
          {loading && (
            <CircularProgress
              size={24}
              sx={{
                color: "primary.main",
                position: "absolute",
                top: "50%",
                left: "50%",
                marginTop: "-12px",
                marginLeft: "-12px",
              }}
            />
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default UserSubscription;
